import React from "react";
import { motion, AnimatePresence, useAnimationControls, useMotionValue, animate } from "framer-motion";
import { playSquish } from "@/lib/audio";

// ============== Cursor-tracking hook ==============
// Tracks the mouse globally and returns an SVG-coordinate offset `{x, y}` that
// each pupil should translate by, clamped to a small radius so Mochi looks at
// the cursor without "popping" out of the eye. The offset is computed relative
// to the avatar's center on screen and re-measured continuously (works even
// if the avatar is floating from its mood animation).
function useEyeTracking(containerRef, maxRadius = 4) {
    const dx = useMotionValue(0);
    const dy = useMotionValue(0);

    React.useEffect(() => {
        let raf = 0;
        let pending = null;
        const onMove = (e) => {
            const node = containerRef.current;
            if (!node) return;
            const rect = node.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height * 0.42;  // approx eye-line within avatar
            let vx = (e.clientX - cx) / (rect.width / 2);   // -1..1
            let vy = (e.clientY - cy) / (rect.height / 2);
            // Soft clamp to unit circle (no awkward corner snapping)
            const mag = Math.sqrt(vx * vx + vy * vy);
            if (mag > 1) { vx /= mag; vy /= mag; }
            pending = { x: vx * maxRadius, y: vy * maxRadius };
            if (!raf) raf = requestAnimationFrame(flush);
        };
        const flush = () => {
            raf = 0;
            if (pending) {
                animate(dx, pending.x, { duration: 0.18, ease: "easeOut" });
                animate(dy, pending.y, { duration: 0.18, ease: "easeOut" });
                pending = null;
            }
        };
        const onLeave = () => {
            animate(dx, 0, { duration: 0.45, ease: "easeOut" });
            animate(dy, 0, { duration: 0.45, ease: "easeOut" });
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerleave", onLeave);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerleave", onLeave);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [containerRef, maxRadius, dx, dy]);

    return { dx, dy };
}

/**
 * Mametchi-inspired Tamagotchi avatar.
 * Yellow teardrop body, two big black ear tufts, huge expressive eyes,
 * pink blushy cheeks, open-mouth smiles. SVG + framer-motion driven.
 */

const MOODS = {
    happy:      { body: "#FFE36E", ear: "#1B1B1B", cheek: "#FFA8A0", anim: { y: [0, -6, 0] }, dur: 3.6 },
    excited:    { body: "#FFD93B", ear: "#1B1B1B", cheek: "#FF8E83", anim: { y: [0, -16, 0], rotate: [0, -3, 3, 0] }, dur: 0.7 },
    sleepy:     { body: "#FFE9A8", ear: "#2A2A2A", cheek: "#F2B8AE", anim: { y: [0, -2, 0], scale: [1, 1.02, 1] }, dur: 5.5 },
    sad:        { body: "#F5DDA0", ear: "#2A2A2A", cheek: "#E5A89E", anim: { y: [0, -3, 0], scale: 0.96 }, dur: 5 },
    proud:      { body: "#FFD23F", ear: "#1B1B1B", cheek: "#FF8E83", anim: { y: [0, -8, 0], scale: 1.06 }, dur: 3 },
    worried:    { body: "#F5DCB0", ear: "#2A2A2A", cheek: "#E5A89E", anim: { y: [0, -4, 0], rotate: [0, -2, 2, 0] }, dur: 2.4 },
    energetic:  { body: "#FFC931", ear: "#1B1B1B", cheek: "#FF8073", anim: { y: [0, -20, 0] }, dur: 0.5 },
    frustrated: { body: "#FFC18A", ear: "#2A2A2A", cheek: "#E55E4F", anim: { x: [0, -3, 3, -3, 3, 0], y: [0, -3, 0] }, dur: 0.45 },
    cozy:       { body: "#FFE08A", ear: "#2A2A2A", cheek: "#F2B0A6", anim: { y: [0, -3, 0], rotate: [0, -1, 1, 0] }, dur: 6 },
    // NEW personality emotions (Mochi Personality Bible)
    hype:       { body: "#FFD23F", ear: "#1B1B1B", cheek: "#FF8E83", anim: { y: [0, -22, 0], rotate: [0, -6, 6, -6, 6, 0], scale: [1, 1.05, 1] }, dur: 0.55, fx: "sparkles" },
    party:      { body: "#FFD23F", ear: "#1B1B1B", cheek: "#FF7A6E", anim: { y: [0, -18, 0], rotate: [0, -8, 8, 0], scale: [1, 1.08, 1] }, dur: 0.5, fx: "confetti" },
    determined: { body: "#FFC931", ear: "#1B1B1B", cheek: "#FF8073", anim: { y: [0, -4, 0], scale: [1, 1.04, 1] }, dur: 1.4, fx: "pawsUp" },
    // Wink — used exclusively when Mochi uses her cover pass. Subtle bounce,
    // happy body palette. The actual one-eye closure is rendered in <Eyes>.
    wink:       { body: "#FFD93B", ear: "#1B1B1B", cheek: "#FF8E83", anim: { y: [0, -6, 0], rotate: [0, -1, 1, 0] }, dur: 1.6 },
};

// ============== Eyes ==============
function Eyes({ mood, gaze, idleGaze }) {
    const cx1 = 80, cx2 = 130, cy = 92;
    const eyeW = 19, eyeH = 24;

    if (mood === "sleepy" || mood === "cozy") {
        return (
            <g>
                <path d={`M${cx1 - 12} ${cy} q12 8 24 0`} stroke="#1B1B1B" strokeWidth="4" fill="none" strokeLinecap="round" />
                <path d={`M${cx2 - 12} ${cy} q12 8 24 0`} stroke="#1B1B1B" strokeWidth="4" fill="none" strokeLinecap="round" />
                {/* lashes */}
                <path d={`M${cx1 - 12} ${cy + 4} l-3 3`} stroke="#1B1B1B" strokeWidth="2.5" strokeLinecap="round" />
                <path d={`M${cx2 + 12} ${cy + 4} l3 3`} stroke="#1B1B1B" strokeWidth="2.5" strokeLinecap="round" />
            </g>
        );
    }

    if (mood === "happy" || mood === "proud" || mood === "determined") {
        // Big iconic black ovals with white shines (the classic Tamagotchi look)
        return (
            <g>
                <BigEye cx={cx1} cy={cy} w={eyeW} h={eyeH} blink gaze={gaze} idleGaze={idleGaze} />
                <BigEye cx={cx2} cy={cy} w={eyeW} h={eyeH} blink gaze={gaze} idleGaze={idleGaze} />
            </g>
        );
    }

    if (mood === "wink") {
        // LEFT eye closes into a curved arc (the wink). Right eye stays open
        // with a slight upward arch for that conspiratorial half-smile.
        // Exists nowhere else in the app — purely the cover-pass moment.
        return (
            <g>
                <path
                    d={`M${cx1 - 13} ${cy + 2} q13 -10 26 0`}
                    stroke="#1B1B1B"
                    strokeWidth="4.5"
                    fill="none"
                    strokeLinecap="round"
                />
                <BigEye cx={cx2} cy={cy} w={eyeW} h={eyeH} gaze={gaze} idleGaze={idleGaze} />
            </g>
        );
    }

    if (mood === "excited" || mood === "hype" || mood === "party") {
        return (
            <g>
                <SparkleEye cx={cx1} cy={cy} />
                <SparkleEye cx={cx2} cy={cy} />
            </g>
        );
    }

    if (mood === "frustrated") {
        return (
            <g>
                <path d={`M${cx1 - 11} ${cy - 6} l11 6 -11 6`} stroke="#1B1B1B" strokeWidth="4.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                <path d={`M${cx2 + 11} ${cy - 6} l-11 6 11 6`} stroke="#1B1B1B" strokeWidth="4.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        );
    }

    if (mood === "sad") {
        return (
            <g>
                <BigEye cx={cx1} cy={cy + 2} w={eyeW - 2} h={eyeH - 4} droop gaze={gaze} idleGaze={idleGaze} />
                <BigEye cx={cx2} cy={cy + 2} w={eyeW - 2} h={eyeH - 4} droop gaze={gaze} idleGaze={idleGaze} />
                {/* tear */}
                <motion.path
                    d={`M${cx1 - 8} ${cy + 12} q-2 8 0 14 q4 -6 0 -14 z`}
                    fill="#7BC9E8"
                    stroke="#3596C2"
                    strokeWidth="1.2"
                    animate={{ y: [0, 4, 0], opacity: [0.85, 1, 0.85] }}
                    transition={{ duration: 2.2, repeat: Infinity }}
                />
            </g>
        );
    }

    if (mood === "worried") {
        return (
            <g>
                <BigEye cx={cx1} cy={cy + 1} w={eyeW - 3} h={eyeH - 6} gaze={gaze} idleGaze={idleGaze} />
                <BigEye cx={cx2} cy={cy + 1} w={eyeW - 3} h={eyeH - 6} gaze={gaze} idleGaze={idleGaze} />
                {/* eyebrows tilted */}
                <path d={`M${cx1 - 13} ${cy - 18} q13 -3 24 5`} stroke="#1B1B1B" strokeWidth="3.5" fill="none" strokeLinecap="round" />
                <path d={`M${cx2 - 11} ${cy - 13} q11 -8 24 -5`} stroke="#1B1B1B" strokeWidth="3.5" fill="none" strokeLinecap="round" />
            </g>
        );
    }

    // energetic + default
    return (
        <g>
            <BigEye cx={cx1} cy={cy} w={eyeW} h={eyeH} blink gaze={gaze} idleGaze={idleGaze} />
            <BigEye cx={cx2} cy={cy} w={eyeW} h={eyeH} blink gaze={gaze} idleGaze={idleGaze} />
        </g>
    );
}

function BigEye({ cx, cy, w, h, blink, droop, gaze, idleGaze }) {
    // Stack the cursor-tracking gaze (MotionValues) and the idle micro-glance
    // (plain numbers) on TWO nested motion groups. Framer auto-springs the
    // idle layer based on its `animate` prop, while the cursor layer reads
    // raw MotionValues — combining them at render time without coupling.
    return (
        <motion.g
            animate={blink ? { scaleY: [1, 1, 0.08, 1] } : undefined}
            transition={blink ? { duration: 4.6, times: [0, 0.93, 0.96, 1], repeat: Infinity } : undefined}
            style={{ transformOrigin: `${cx}px ${cy}px` }}
        >
            {/* Idle-glance layer (200ms ease-in/out via Framer transition) */}
            <motion.g
                animate={{ x: idleGaze?.x || 0, y: idleGaze?.y || 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
            >
                {/* Cursor gaze layer — only the pupil + shines translate, not
                    the brow/droop overlay so brows stay anchored to the face */}
                <motion.g style={gaze ? { x: gaze.dx, y: gaze.dy } : undefined}>
                    <ellipse cx={cx} cy={cy} rx={w} ry={h} fill="#1B1B1B" />
                    {/* primary white shine */}
                    <ellipse cx={cx + w * 0.35} cy={cy - h * 0.35} rx={w * 0.32} ry={h * 0.28} fill="#FFFFFF" />
                    {/* tiny secondary shine */}
                    <ellipse cx={cx - w * 0.3} cy={cy + h * 0.3} rx={w * 0.18} ry={h * 0.14} fill="#FFFFFF" opacity="0.85" />
                </motion.g>
            </motion.g>
            {droop && (
                <path d={`M${cx - w} ${cy - h - 2} q${w} -6 ${w * 2} 4`} stroke="#1B1B1B" strokeWidth="3" fill="none" strokeLinecap="round" />
            )}
        </motion.g>
    );
}

function SparkleEye({ cx, cy }) {
    const w = 19, h = 24;
    // 4-point sparkle inside an ellipse
    const star = (size) => {
        const pts = [];
        for (let i = 0; i < 8; i++) {
            const r = i % 2 === 0 ? size : size * 0.42;
            const a = (Math.PI / 4) * i - Math.PI / 2;
            pts.push(`${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`);
        }
        return pts.join(" ");
    };
    return (
        <g>
            <ellipse cx={cx} cy={cy} rx={w} ry={h} fill="#1B5BA8" />
            <ellipse cx={cx} cy={cy} rx={w * 0.78} ry={h * 0.78} fill="#3CA3DB" />
            <polygon points={star(13)} fill="#FFFFFF" />
            <ellipse cx={cx + 4} cy={cy - 7} rx="3" ry="4" fill="#FFFFFF" />
        </g>
    );
}

// ============== Mouth ==============
function Mouth({ mood }) {
    const cx = 105, cy = 128;

    if (mood === "sleepy" || mood === "cozy") {
        return <path d={`M${cx - 8} ${cy - 4} q8 6 16 0`} stroke="#1B1B1B" strokeWidth="3.5" fill="none" strokeLinecap="round" />;
    }

    if (mood === "sad") {
        return <path d={`M${cx - 10} ${cy + 4} q10 -10 20 0`} stroke="#1B1B1B" strokeWidth="3.5" fill="none" strokeLinecap="round" />;
    }

    if (mood === "worried") {
        return <path d={`M${cx - 12} ${cy} q6 6 12 0 q6 -6 12 0`} stroke="#1B1B1B" strokeWidth="3.5" fill="none" strokeLinecap="round" />;
    }

    if (mood === "frustrated") {
        return (
            <g>
                <path d={`M${cx - 11} ${cy + 4} q11 -2 22 0`} stroke="#1B1B1B" strokeWidth="3.5" fill="none" strokeLinecap="round" />
                <path d={`M${cx - 6} ${cy - 2} l4 -3 4 3 4 -3 4 3`} stroke="#1B1B1B" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        );
    }

    // happy / excited / proud / energetic / hype / party / determined — iconic open-mouth smile
    const isBig = mood === "excited" || mood === "energetic" || mood === "hype" || mood === "party";
    const open = isBig ? 22 : mood === "proud" ? 16 : 14;
    const w = isBig ? 30 : 24;
    return (
        <g>
            {/* outer mouth */}
            <path
                d={`M${cx - w} ${cy - 4}
                    Q${cx} ${cy + open + 6} ${cx + w} ${cy - 4}
                    Q${cx} ${cy + 2} ${cx - w} ${cy - 4} Z`}
                fill="#1B1B1B"
            />
            {/* tongue */}
            <path
                d={`M${cx - w * 0.55} ${cy + 2}
                    Q${cx} ${cy + open + 2} ${cx + w * 0.55} ${cy + 2}
                    Q${cx} ${cy + open * 0.45} ${cx - w * 0.55} ${cy + 2} Z`}
                fill="#FF6B7A"
            />
            {/* tongue highlight */}
            <ellipse cx={cx - w * 0.18} cy={cy + open * 0.45} rx={w * 0.18} ry="2" fill="#FFB1B7" opacity="0.9" />
        </g>
    );
}

// ============== Accessories ==============
function Sparkle({ cx, cy, size = 6, color = "#FFD23F" }) {
    return (
        <path
            d={`M${cx} ${cy - size} L${cx + size * 0.25} ${cy - size * 0.25} L${cx + size} ${cy} L${cx + size * 0.25} ${cy + size * 0.25} L${cx} ${cy + size} L${cx - size * 0.25} ${cy + size * 0.25} L${cx - size} ${cy} L${cx - size * 0.25} ${cy - size * 0.25} Z`}
            fill={color}
            stroke="#1B1B1B"
            strokeWidth="1"
        />
    );
}

function Accessories({ mood }) {
    if (mood === "sleepy") {
        return (
            <g>
                {[0, 1, 2].map((i) => (
                    <motion.text
                        key={i}
                        x={170 + i * 9}
                        y={50 - i * 14}
                        fontFamily="Fredoka"
                        fontWeight="700"
                        fontSize={16 - i * 3}
                        fill="#5D7A7E"
                        animate={{ opacity: [0, 1, 0], y: [50 - i * 14, 22 - i * 14] }}
                        transition={{ duration: 2.6, repeat: Infinity, delay: i * 0.5 }}
                    >z</motion.text>
                ))}
            </g>
        );
    }
    if (mood === "excited" || mood === "proud" || mood === "energetic" || mood === "hype" || mood === "determined") {
        const sparkleCount = mood === "hype" ? 8 : 4;
        const sparkles = [
            { x: 28, y: 32, d: 0, c: "#FFD23F" },
            { x: 178, y: 50, d: 0.4, c: "#FF8E83" },
            { x: 36, y: 150, d: 0.8, c: "#9CB4A6" },
            { x: 175, y: 140, d: 1.2, c: "#FFD23F" },
            { x: 18, y: 90, d: 0.2, c: "#FFB347" },
            { x: 188, y: 100, d: 0.6, c: "#9CB4A6" },
            { x: 50, y: 18, d: 1.0, c: "#FF8E83" },
            { x: 160, y: 20, d: 1.4, c: "#FFD23F" },
        ].slice(0, sparkleCount);
        return (
            <g>
                {sparkles.map((s) => (
                    // Static array → coordinates form a stable identity for
                    // each sparkle. Avoids array-index-key linter noise without
                    // changing render behaviour.
                    <motion.g key={`spk-${s.x}-${s.y}`} animate={{ scale: [0, 1, 0], rotate: [0, 180] }} transition={{ duration: 1.6, repeat: Infinity, delay: s.d }}>
                        <Sparkle cx={s.x} cy={s.y} color={s.c} />
                    </motion.g>
                ))}
            </g>
        );
    }
    if (mood === "party") {
        // Confetti rain — falling colored rectangles
        const confetti = [
            { x: 30, c: "#FFD23F", d: 0 },
            { x: 60, c: "#FF8E83", d: 0.2 },
            { x: 90, c: "#9CB4A6", d: 0.4 },
            { x: 120, c: "#D28369", d: 0.1 },
            { x: 150, c: "#E5B96B", d: 0.5 },
            { x: 180, c: "#FFB347", d: 0.3 },
        ];
        return (
            <g>
                {confetti.map((p) => (
                    <motion.rect
                        // Static array → x + colour form a stable identity.
                        key={`conf-${p.x}-${p.c}`}
                        x={p.x} y={0} width={6} height={10} fill={p.c}
                        animate={{ y: [-8, 210], rotate: [0, 360], opacity: [1, 1, 0] }}
                        transition={{ duration: 1.6, repeat: Infinity, delay: p.d }}
                        style={{ originX: `${p.x + 3}px`, originY: "5px" }}
                    />
                ))}
            </g>
        );
    }
    if (mood === "frustrated") {
        // anger steam puffs
        return (
            <g>
                {[0, 1].map((i) => (
                    <motion.g
                        key={i}
                        animate={{ y: [0, -12], opacity: [0, 1, 0] }}
                        transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.4 }}
                    >
                        <path d={`M${30 + i * 145} 50 q-4 -4 0 -8 q4 -2 6 2 q4 -2 4 4 q-2 4 -10 2 z`} fill="#E5E5E5" stroke="#1B1B1B" strokeWidth="1.5" />
                    </motion.g>
                ))}
            </g>
        );
    }
    return null;
}

// ============== Body ==============
function Body({ fill }) {
    // Mametchi-style teardrop body with little arms
    return (
        <g>
            {/* shadow */}
            <ellipse cx="105" cy="195" rx="55" ry="7" fill="rgba(27,27,27,0.18)" />
            {/* main teardrop */}
            <path
                d="M105,40
                   C150,40 175,80 175,120
                   C175,165 150,190 105,190
                   C60,190 35,165 35,120
                   C35,80 60,40 105,40 Z"
                fill={fill}
                stroke="#1B1B1B"
                strokeWidth="3.5"
                strokeLinejoin="round"
            />
            {/* belly highlight */}
            <ellipse cx="105" cy="155" rx="38" ry="14" fill="rgba(255,255,255,0.18)" />
            {/* tiny arms */}
            <path d="M40,140 q-8 6 -10 16 q8 -2 14 -8" fill={fill} stroke="#1B1B1B" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
            <path d="M170,140 q8 6 10 16 q-8 -2 -14 -8" fill={fill} stroke="#1B1B1B" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        </g>
    );
}

// ============== Ears (Mametchi tufts) ==============
function Ears({ fill }) {
    return (
        <g>
            {/* Left ear */}
            <path
                d="M68,38
                   C58,8 38,8 36,30
                   C34,52 56,60 78,50
                   Z"
                fill={fill}
                stroke="#1B1B1B"
                strokeWidth="3.5"
                strokeLinejoin="round"
            />
            {/* Right ear */}
            <path
                d="M142,38
                   C152,8 172,8 174,30
                   C176,52 154,60 132,50
                   Z"
                fill={fill}
                stroke="#1B1B1B"
                strokeWidth="3.5"
                strokeLinejoin="round"
            />
        </g>
    );
}


// Hat / crown — sit ABOVE Mochi's head, between the ears. Renders nothing
// when `accessory` is null. Coordinates target the 210×210 avatar viewBox:
// the ear valley spans roughly x≈80-130 around y=8-46, so we anchor the
// accessory base at y≈47 (just above the eye region).
//
// CRITICAL: this lives INSIDE the motion.svg so it inherits Mochi's bounce
// + the wrapper's per-mood transforms naturally — same pattern as Sunglasses.
function AccessoryHat({ accessory, animateIn = false }) {
    if (!accessory) return null;
    const initial = animateIn ? { y: -16, opacity: 0, rotate: -4 } : false;
    const animate = animateIn ? { y: 0, opacity: 1, rotate: 0 } : { y: 0, opacity: 1, rotate: 0 };
    return (
        <motion.g
            initial={initial}
            animate={animate}
            transition={animateIn
                ? { type: "spring", stiffness: 240, damping: 15, delay: 0.3 }
                : { duration: 0 }}
            style={{ transformOrigin: "105px 47px" }}
        >
            {accessory === "crown" && <CrownPath />}
            {accessory === "birthday-hat" && <BirthdayHatPath />}
        </motion.g>
    );
}

// Crown — symmetric around x=105 (avatar centre), base at y≈47.
function CrownPath() {
    return (
        <g>
            <ellipse cx="105" cy="44" rx="20" ry="3" fill="#FFD23F" opacity="0.25" />
            <rect x="85" y="38" width="40" height="9" rx="2" fill="#F2B632" stroke="#1B1B1B" strokeWidth="1.6" />
            <path
                d="M85 38 L87 21 L96 33 L105 21 L114 33 L123 21 L125 38 Z"
                fill="#FFD23F"
                stroke="#1B1B1B"
                strokeWidth="1.6"
                strokeLinejoin="round"
            />
            <circle cx="87"  cy="25" r="2" fill="#FF7A6E" stroke="#1B1B1B" strokeWidth="0.9" />
            <circle cx="105" cy="25" r="2" fill="#7FB069" stroke="#1B1B1B" strokeWidth="0.9" />
            <circle cx="123" cy="25" r="2" fill="#7C9CBF" stroke="#1B1B1B" strokeWidth="0.9" />
            <circle cx="105" cy="42" r="1.6" fill="#FFFFFF" />
        </g>
    );
}

// Birthday hat — cone + brim + pom-pom, also symmetric around x=105.
function BirthdayHatPath() {
    return (
        <g>
            <circle cx="105" cy="9" r="4" fill="#FF7A6E" stroke="#1B1B1B" strokeWidth="1.6" />
            <circle cx="103.5" cy="8" r="1" fill="#FFFFFF" opacity="0.8" />
            <path
                d="M105 13 L91 44 L119 44 Z"
                fill="#FFD23F"
                stroke="#1B1B1B"
                strokeWidth="1.6"
                strokeLinejoin="round"
            />
            <path d="M98 30 L112 30" stroke="#FF7A6E" strokeWidth="2.4" strokeLinecap="round" />
            <path d="M95 37 L115 37" stroke="#7FB069" strokeWidth="2.4" strokeLinecap="round" />
            <rect x="89" y="43" width="32" height="4" rx="1.4" fill="#F2B632" stroke="#1B1B1B" strokeWidth="1.6" />
            <text x="105" y="26" fontSize="6" textAnchor="middle" fill="#1B1B1B">✦</text>
        </g>
    );
}

// Sakura pin (🌸) — a small 5-petal pink flower tucked behind Mochi's right
// ear (avatar's right = viewer's right, near x=150). Lives INSIDE the motion.svg
// so it rides Mochi's per-mood bounce. Renders alongside any head accessory
// (crown / hat sit centre at x=105) and the sunglasses skin.
function SakuraPin({ animateIn = false }) {
    const cx = 150, cy = 56;
    const petals = [270, 342, 54, 126, 198];
    return (
        <motion.g
            initial={animateIn ? { scale: 0, opacity: 0 } : false}
            animate={{ scale: 1, opacity: 1 }}
            transition={animateIn
                ? { type: "spring", stiffness: 260, damping: 14, delay: 0.2 }
                : { duration: 0 }}
            style={{ transformOrigin: `${cx}px ${cy}px` }}
        >
            {petals.map((deg) => {
                const rad = (deg * Math.PI) / 180;
                const px = cx + Math.cos(rad) * 6;
                const py = cy + Math.sin(rad) * 6;
                return (
                    <ellipse
                        key={deg}
                        cx={px} cy={py} rx="5" ry="3.4"
                        fill="#FFC2D4"
                        stroke="#E78BA8"
                        strokeWidth="0.9"
                        transform={`rotate(${deg + 90} ${px} ${py})`}
                    />
                );
            })}
            <circle cx={cx} cy={cy} r="2.6" fill="#FFD23F" stroke="#E78BA8" strokeWidth="0.6" />
        </motion.g>
    );
}

function Sunglasses({ animateIn = false, gaze, idleGaze }) {
    // Wayfarer-style sunglasses sitting on top of Mochi's eyes.
    // Positioned so the lenses cover the standard eye centers (cx1=85, cx2=125, cy=88).
    // animateIn=true → slide in from above with a tiny bounce; otherwise instant.
    // gaze → MotionValues {dx, dy} so the glossy highlights subtly drift with the cursor
    //        (replacing the now-hidden eye gaze).
    const initial = animateIn ? { y: -40, opacity: 0, rotate: -6 } : false;
    const animate = animateIn ? { y: 0, opacity: 1, rotate: 0 } : { y: 0, opacity: 1, rotate: 0 };
    return (
        <motion.g
            initial={initial}
            animate={animate}
            transition={animateIn ? { type: "spring", stiffness: 220, damping: 14, delay: 0.25 } : { duration: 0 }}
            style={{ transformOrigin: "105px 88px" }}
        >
            {/* Bridge */}
            <path d="M101 86 q4 -2 8 0" stroke="#1B1B1B" strokeWidth="3.5" fill="none" strokeLinecap="round" />
            {/* Left lens */}
            <g>
                <rect x="58" y="72" width="38" height="30" rx="9" ry="11" fill="#1B1B1B" stroke="#1B1B1B" strokeWidth="3" />
                {/* Idle-glance + cursor-gaze stacked, identical to BigEye. */}
                <motion.g
                    animate={{ x: idleGaze?.x || 0, y: idleGaze?.y || 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                    <motion.g style={gaze ? { x: gaze.dx, y: gaze.dy } : undefined}>
                        <path d="M64 78 q4 -3 12 -3 q6 0 10 2 l-3 6 q-7 -3 -14 -1 q-5 1 -7 3 z" fill="#5B6F8A" opacity="0.75" />
                        <ellipse cx="86" cy="94" rx="4" ry="2" fill="#FFFFFF" opacity="0.55" />
                    </motion.g>
                </motion.g>
            </g>
            {/* Right lens */}
            <g>
                <rect x="114" y="72" width="38" height="30" rx="9" ry="11" fill="#1B1B1B" stroke="#1B1B1B" strokeWidth="3" />
                <motion.g
                    animate={{ x: idleGaze?.x || 0, y: idleGaze?.y || 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                    <motion.g style={gaze ? { x: gaze.dx, y: gaze.dy } : undefined}>
                        <path d="M120 78 q4 -3 12 -3 q6 0 10 2 l-3 6 q-7 -3 -14 -1 q-5 1 -7 3 z" fill="#5B6F8A" opacity="0.75" />
                        <ellipse cx="142" cy="94" rx="4" ry="2" fill="#FFFFFF" opacity="0.55" />
                    </motion.g>
                </motion.g>
            </g>
            {/* Tiny outer hinges */}
            <rect x="50" y="84" width="9" height="4" rx="1.5" fill="#1B1B1B" />
            <rect x="151" y="84" width="9" height="4" rx="1.5" fill="#1B1B1B" />
        </motion.g>
    );
}

export default function Avatar({ mood = "happy", size = 220, onTap, style = "feminine", skin = null, animateSkinIn = false, momentum = 0, boost = 0, idleGaze, accessory = null, accessoryAnimateIn = false, pin = false }) {
    const m = MOODS[mood] || MOODS.happy;
    const containerRef = React.useRef(null);
    const gaze = useEyeTracking(containerRef, 4);

    // ---- Momentum-driven idle animation tuning -------------------------
    // `momentum` is a 0..1 scalar from Mochi's Hype Meter. Higher meter →
    // faster idle breathing/bouncing → more excited-looking Mochi.
    //   momentum=0   → 1.0× base duration (calm)
    //   momentum=1   → ~0.55× base duration (~1.8× faster, very animated)
    // `boost` is a transient +amplitude multiplier (0..1) applied on top of
    // momentum — currently fired during the 6-second fire-dance window so
    // Mochi visibly jumps higher when the user taps the Inferno pill.
    const baseDur = m.dur || 3;
    const animDur = baseDur / (1 + momentum * 0.85 + boost * 0.6);
    // Scale the y-bounce keyframes when boost > 0 (preserves x/rotate/scale).
    const tunedAnim = React.useMemo(() => {
        if (!boost || !Array.isArray(m.anim?.y)) return m.anim;
        const scale = 1 + boost;
        return { ...m.anim, y: m.anim.y.map((v) => v * scale) };
    }, [m.anim, boost]);
    // Soft outer halo intensifies at higher momentum (Blaze/Inferno glow)
    const haloOpacity = 0.25 + momentum * 0.55;

    // Transient tap SQUISH — volume-preserving asymmetric deformation.
    // Mochi flattens vertically + widens horizontally (like pressing a soft
    // blob), then over-rebounds tall before settling. No rotation — pure
    // squish physics. Anchored at the feet so it feels like a real press.
    const tapControls = useAnimationControls();
    const handleClick = () => {
        try { playSquish(); } catch { /* ignore */ }
        tapControls.start({
            scaleX: [1, 1.22, 0.94, 1.06, 0.99, 1],
            scaleY: [1, 0.78, 1.08, 0.96, 1.01, 1],
            transition: { duration: 0.50, ease: [0.22, 1.2, 0.36, 1], times: [0, 0.18, 0.42, 0.65, 0.85, 1] },
        });
        onTap?.();
    };

    return (
        <div
            ref={containerRef}
            className="relative inline-flex items-center justify-center select-none cursor-pointer"
            style={{ width: size, height: size }}
            data-testid={`avatar-${mood}`}
            onClick={handleClick}
        >
            <div
                className="absolute inset-0 rounded-full avatar-halo blur-2xl transition-opacity duration-700"
                style={{ opacity: haloOpacity }}
            />

            <AnimatePresence mode="wait">
                <motion.div
                    key={mood}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.85 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20 }}
                    className="relative"
                    style={{ width: size, height: size }}
                >
                    <motion.div animate={tapControls} style={{ transformOrigin: "50% 70%" }}>
                        <motion.svg
                            viewBox="0 0 210 210"
                            width={size}
                            height={size}
                            animate={tunedAnim}
                            transition={{ duration: animDur, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <Ears fill={m.ear} />
                            <Body fill={m.body} />

                            {/* Cheeks (feminine only) */}
                            {style === "feminine" && (
                                <>
                                    <ellipse cx="60" cy="135" rx="13" ry="8" fill={m.cheek} opacity="0.95" />
                                    <ellipse cx="150" cy="135" rx="13" ry="8" fill={m.cheek} opacity="0.95" />
                                </>
                            )}
                            {/* Eyes are hidden behind sunglasses; the lens highlights take over gaze duty */}
                            {skin !== "sunglasses" && <Eyes mood={mood} gaze={gaze} idleGaze={idleGaze} />}
                            <Mouth mood={mood} />

                            {skin === "sunglasses" && <Sunglasses animateIn={animateSkinIn} gaze={gaze} idleGaze={idleGaze} />}

                            {/* Sakura pin — drawn before the head accessory so a
                                centre crown/hat layers cleanly on top if they ever meet. */}
                            {pin && <SakuraPin />}

                            {/* Milestone accessory (crown / birthday hat) — drawn LAST so it
                                renders on top of ears + skin. Lives inside the SVG so it
                                inherits Mochi's bounce/rotate/scale per-mood transforms. */}
                            <AccessoryHat accessory={accessory} animateIn={accessoryAnimateIn} />

                            <Accessories mood={mood} />
                        </motion.svg>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
